import { useEffect, useRef, useState } from "react";
import { getSocket } from "./socketClient";

// Subscribes to the backend's "payment:updated" notifications (emitted from
// Mongo change streams, so they fire for webhooks, customer-app payments and
// admin actions alike). Events are only a nudge - consumers refetch over REST.
//
//   bookingId  optional: only events for this booking are delivered
//   onEvent    (payload) => void, called for every matching event
//   onResync   () => void, called after a socket reconnect and, while the
//              socket is down, every POLL_MS so the screen never goes stale
//
// Returns { connected } for a Live / Reconnecting indicator.
const POLL_MS = 15000;

export default function usePaymentEvents({ bookingId, onEvent, onResync } = {}) {
    const [connected, setConnected] = useState(() => !!getSocket().connected);
    const eventRef = useRef(onEvent);
    const resyncRef = useRef(onResync);
    eventRef.current = onEvent;
    resyncRef.current = onResync;

    useEffect(() => {
        const socket = getSocket();
        let wasConnected = !!socket.connected;
        let timer = null;

        const stopPoll = () => { if (timer) { clearInterval(timer); timer = null; } };
        const startPoll = () => {
            if (timer) return;
            timer = setInterval(() => resyncRef.current && resyncRef.current(), POLL_MS);
        };

        const handleEvent = (payload) => {
            if (!payload) return;
            if (bookingId && payload.bookingId !== bookingId) return;
            if (eventRef.current) eventRef.current(payload);
        };
        const handleConnect = () => {
            setConnected(true);
            stopPoll();
            // anything missed while offline is picked up by one refetch
            if (!wasConnected && resyncRef.current) resyncRef.current();
            wasConnected = true;
        };
        const handleDisconnect = () => {
            wasConnected = false;
            setConnected(false);
            startPoll();
        };

        socket.on("payment:updated", handleEvent);
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        if (!socket.connected) startPoll();
        setConnected(!!socket.connected);

        return () => {
            stopPoll();
            socket.off("payment:updated", handleEvent);
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
        };
    }, [bookingId]);

    return { connected };
}
